import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import _compact from 'lodash/compact';
import { useRecoilValue } from 'recoil';
import { useDynamicEbillsImportCopy } from '@melio/ap-activities/src/components/ebills-import/EbillsImport.activity';
import { Group, Icon, IconButton, Text, useBreakpoint } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { useAccount } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { usePermissions } from '@melio/platform-permissions';
import { useConfig } from '@melio/platform-provider';

import { SettingsCardIdEnum, SettingsCardProps } from '@/cl/components/SettingsCard/SettingsCard.component';
import {
  AccountingSoftwareSyncIcon,
  BillingIcon,
  CompanyIcon,
  PaymentMethodsIcon,
  ProfileIcon,
  ReceivingMethodsIcon,
  SupportChatIcon,
} from '@/cl/icons/system';
import { ScreenSplitLayout } from '@/cl/layouts/screen-split-layout/screenSplitLayout.component';
import { SettingsNestedPages } from '@/consts/SettingsConsts';
import { WithLoading } from '@/hoc/withLoading.hoc';
import { usePartnerConfig } from '@/hooks/partners';
import { useRouter } from '@/hooks/router.hooks';
import { useActiveScreen } from '@/hooks/useActiveScreen';
import { useHasAccessToFirm } from '@/hooks/useHasAccessToFirm.hooks';
import { useIsOrganizationInMsnOnly } from '@/hooks/useIsOrganizationInMsnOnly.hooks';
import { ScreensEnum } from '@/store/app/app.types';
import {
  settingsSelectedSettingsPageSelector,
  settingsState,
  useSettingsSelectedSettingsPage,
} from '@/store/Settings/Settings.model';
import { usePlatformIntl } from '@/translations/Intl';
import { useShouldShowDeliveryMethods } from '@/utils/receivingMethods.utils';
import { SettingsSubPageHeading } from '@/widgets/settings-page/Common/SettingsSubPageHeading.widget';
import { SettingsGroupWidget } from '@/widgets/settings-page/SettingsHomePage/SettingsGroup.widget';

const SettingsLayoutRoute = () => {
  const { track } = useAnalytics();
  const [accountManagerAccessCollaboratorsEnabled] = useFeature<boolean>(
    FeatureFlags.AccountManagerAccessCollaborators,
    false,
  );
  const [notificationPreferencesEnabled] = useFeature<boolean>(FeatureFlags.NotificationPreferences, true);
  const [displayReceivingMethodSettingItem] = useFeature<boolean>(FeatureFlags.DisplayReceivingMethodsItem, false);
  const [isApprovalWorkflowsFeatureEnabled] = useFeature(FeatureFlags.ApprovalWorkflows, false);
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const {
    partnerConfig: { allowEditFirstAndLastName },
  } = usePartnerConfig();
  const {
    settings: { isEmbeddedExperience, isPaymentApprovalLimitEnabled },
  } = useConfig();
  const hasAccessToFirm = useHasAccessToFirm();

  const { can } = usePermissions();
  useActiveScreen(ScreensEnum.settings);
  const { formatMessage } = usePlatformIntl();
  const {
    goToSettingsProfile,
    goToSettingsCompany,
    goToSettingsNotificationPreferences,
    goToSettingsAccountSoftware,
    goToSettingsPaymentMethods,
    goToSettingsBilling,
    goToSettingsSupport,
    goToSettingsCollaborators,
    goToReceivingMethods,
    goToSettingsWorkflows,
    goToNewDashboardTab,
    goToSettingsSubscriptionPlans,
  } = useRouter();

  const { accountingSettingsCardCopy } = useDynamicEbillsImportCopy();

  const selectedSettingsPage = useRecoilValue(settingsSelectedSettingsPageSelector);

  const isPureMSNOrganization = useShouldShowDeliveryMethods();

  const isShowReceivingMethodsItem = useIsOrganizationInMsnOnly();

  const approvalWorkflowsEnabled =
    isApprovalWorkflowsFeatureEnabled && can({ action: 'read', subject: 'approvalWorkflow' });

  const CompanySettings: Array<SettingsCardProps> = _compact([
    {
      id: SettingsCardIdEnum.ProfileSettings,
      icon: <ProfileIcon />,
      content: formatMessage('app.settings.companySection.cards.profile.title'),
      helperText: formatMessage(
        `app.settings.companySection.cards.profile.description${allowEditFirstAndLastName ? '' : '.uneditable'}`,
      ),
      onClick: goToSettingsProfile,
      isSelected: selectedSettingsPage === SettingsNestedPages.PROFILE,
      cardName: 'profile',
    },
    hasAccessToFirm
      ? {
          id: SettingsCardIdEnum.CompanySettings,
          icon: <CompanyIcon />,
          content: formatMessage('app.settings.companySection.cards.company.title'),
          helperText: formatMessage('app.settings.companySection.cards.company.description'),
          onClick: goToSettingsCompany,
          isSelected: selectedSettingsPage === SettingsNestedPages.COMPANY,
          cardName: 'company',
        }
      : null,
    accountManagerAccessCollaboratorsEnabled &&
    hasAccessToFirm &&
    (can({ subject: 'collaborator', action: 'read' }) || can({ subject: 'invitation', action: 'read' }))
      ? {
          id: SettingsCardIdEnum.CollaboratorsSetting,
          icon: <Icon type="user-add" />,
          content: formatMessage('app.settings.companySection.cards.collaborators.title'),
          helperText: formatMessage('app.settings.companySection.cards.collaborators.description'),
          onClick: goToSettingsCollaborators,
          isSelected: selectedSettingsPage === SettingsNestedPages.COLLABORATORS,
          cardName: 'collaborators',
        }
      : null,
    approvalWorkflowsEnabled && hasAccessToFirm && !isPaymentApprovalLimitEnabled
      ? {
          id: SettingsCardIdEnum.WorkflowsSetting,
          icon: <Icon type="flow" />,
          content: formatMessage('app.settings.companySection.cards.workflows.title'),
          helperText: formatMessage('app.settings.companySection.cards.workflows.description'),
          onClick: goToSettingsWorkflows,
          isSelected: selectedSettingsPage === SettingsNestedPages.WORKFLOWS,
          cardName: 'approval-workflows',
        }
      : null,
    notificationPreferencesEnabled && hasAccessToFirm
      ? {
          id: SettingsCardIdEnum.NotificationPreferences,
          icon: <Icon type="notification" />,
          content: formatMessage('app.settings.companySection.cards.notificationPreferences.title'),
          helperText: formatMessage('app.settings.companySection.cards.notificationPreferences.description'),
          onClick: goToSettingsNotificationPreferences,
          isSelected: selectedSettingsPage === SettingsNestedPages.NOTIFICATION_PREFERENCES,
          cardName: 'notification-preferences',
        }
      : null,
    hasAccessToFirm &&
    (can({ subject: 'accountingPlatform:sync', action: 'create' }) ||
      can({ subject: 'accountingPlatform', action: 'delete' }))
      ? {
          id: SettingsCardIdEnum.AccountingSoftwareSync,
          icon: <AccountingSoftwareSyncIcon />,
          content: formatMessage('app.settings.companySection.cards.accounting.title'),
          helperText: accountingSettingsCardCopy,
          onClick: goToSettingsAccountSoftware,
          isSelected: selectedSettingsPage === SettingsNestedPages.ACCOUNT_SOFTWARE,
          cardName: 'accounting-software',
        }
      : null,
  ]);
  const PaymentSettings: Array<SettingsCardProps> = _compact([
    {
      id: SettingsCardIdEnum.PaymentMethodsSetting,
      icon: <PaymentMethodsIcon />,
      content: formatMessage('app.settings.BillingSection.cards.methods.title'),
      helperText: formatMessage('app.settings.BillingSection.cards.methods.description'),
      onClick: goToSettingsPaymentMethods,
      isSelected: selectedSettingsPage === SettingsNestedPages.PAYMENT_METHODS,
      cardName: 'payment-methods',
    },
    (isShowReceivingMethodsItem || isPureMSNOrganization) && displayReceivingMethodSettingItem
      ? {
          id: SettingsCardIdEnum.ReceivingMethodsSettings,
          icon: <ReceivingMethodsIcon />,
          content: formatMessage('app.settings.BillingSection.cards.receivingMethods.title'),
          helperText: formatMessage('app.settings.BillingSection.cards.receivingMethods.description'),
          onClick: goToReceivingMethods,
          isSelected: selectedSettingsPage === SettingsNestedPages.RECEIVING_METHODS,
          cardName: 'receiving-methods',
        }
      : null,
    isPlatformSubscriptionsEnabled
      ? {
          id: SettingsCardIdEnum.SubscriptionPlansSettings,
          icon: <ReceivingMethodsIcon />,
          content: formatMessage(`app.settings.BillingSection.cards.subscriptionPlans.title`),
          helperText: formatMessage(`app.settings.BillingSection.cards.subscriptionPlans.description`),
          onClick: goToSettingsSubscriptionPlans,
          isSelected: selectedSettingsPage === SettingsNestedPages.SUBSCRIPTION_PLANS,
          cardName: 'subscription-plans',
        }
      : null,
    {
      id: SettingsCardIdEnum.BillingSetting,
      icon: <BillingIcon />,
      content: formatMessage('app.settings.BillingSection.cards.billing.title'),
      helperText: formatMessage('app.settings.BillingSection.cards.billing.description'),
      onClick: goToSettingsBilling,
      isSelected: selectedSettingsPage === SettingsNestedPages.BILLING,
      cardName: 'billing',
    },
  ]);
  const SupportSettings: Array<SettingsCardProps> = [
    {
      id: SettingsCardIdEnum.SupportSetting,
      icon: <SupportChatIcon />,
      content: formatMessage('app.settings.SupportSection.cards.support.title'),
      helperText: formatMessage('app.settings.SupportSection.cards.support.description'),
      onClick: goToSettingsSupport,
      isSelected: selectedSettingsPage === SettingsNestedPages.SUPPORT,
      cardName: 'support',
    },
  ];

  const groups = _compact([
    { title: formatMessage('app.settings.companySection.title'), settings: CompanySettings },
    hasAccessToFirm ? { title: formatMessage('app.settings.BillingSection.title'), settings: PaymentSettings } : null,
    { title: formatMessage('app.settings.SupportSection.title'), settings: SupportSettings },
  ]);

  useEffect(() => {
    track(`SettingsPage`, 'View');
  }, [track]);

  return (
    <Group spacing="s" variant="vertical" data-testid="settings-screen">
      <Group alignItems="center">
        {isEmbeddedExperience ? (
          <IconButton
            variant="naked"
            aria-label={formatMessage('app.settings.backButton.ariaLabel')}
            data-testid="back-button"
            icon="chevron-left"
            onClick={() => goToNewDashboardTab('vendors')}
          />
        ) : null}
        <Text as="h2" textStyle="heading1Semi">
          {formatMessage('app.settings.title')}
        </Text>
      </Group>
      <Group variant="vertical">
        {groups.map((group) => (
          <SettingsGroupWidget key={group.title} title={group.title} settings={group.settings} />
        ))}
      </Group>
    </Group>
  );
};

export const SettingsScreen = () => {
  const location = useLocation();
  const { isExtraSmallScreen } = useBreakpoint();
  const selectedPage = useRecoilValue(settingsState);
  const setSelectedSettingsPage = useSettingsSelectedSettingsPage();
  const { isLoading, data } = useAccount({ id: 'me' });

  useEffect(() => {
    if (new RegExp(`^/settings/${SettingsNestedPages.COMPANY}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.COMPANY);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.COLLABORATORS}(/.*)?$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.COLLABORATORS);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.ACCOUNT_SOFTWARE}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.ACCOUNT_SOFTWARE);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.PAYMENT_METHODS}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.PAYMENT_METHODS);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.BILLING}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.BILLING);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.SUPPORT}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.SUPPORT);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.NOTIFICATION_PREFERENCES}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.NOTIFICATION_PREFERENCES);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.PROFILE}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.PROFILE);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.RECEIVING_METHODS}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.RECEIVING_METHODS);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.WORKFLOWS}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.WORKFLOWS);
      return;
    } else if (new RegExp(`^/settings/${SettingsNestedPages.SUBSCRIPTION_PLANS}$`).test(location.pathname)) {
      setSelectedSettingsPage(SettingsNestedPages.SUBSCRIPTION_PLANS);
      return;
    } else {
      if (isExtraSmallScreen) setSelectedSettingsPage(null);
      else setSelectedSettingsPage(SettingsNestedPages.PROFILE);
      return;
    }
  }, [location.pathname, isExtraSmallScreen, setSelectedSettingsPage]);

  const secondScreen = (
    <WithLoading isLoading={isLoading}>
      <SettingsSubPageHeading displayName={`${data?.user.firstName} ${data?.user.lastName}`} />
      <Outlet />
    </WithLoading>
  );

  return (
    <ScreenSplitLayout
      {...(isExtraSmallScreen && { showScreenMobile: selectedPage.selectedSettingsPage ? 'second' : 'first' })}
      firstScreen={<SettingsLayoutRoute />}
      secondScreen={secondScreen}
    />
  );
};
